/** The payroll API as React Query hooks. The calculation is server-side (`services/payroll.py`) so every reviewer sees the same figure. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import {
  toFrozenSegment,
  type FrozenSegment,
  type FrozenSegmentWire,
} from './frozenSegment'
import { closeWroteNothing, type PayrollMonth } from './payrollClose'
import type { TaskRuleSet } from './taskRules'
import { timeKeys } from './time'

export type PayRun = 'advance' | 'final'

/** One sentence of a why-note: a template key and its figures, or text no template matched. */
export type PayNotePart = { key: string; params: Record<string, string> } | { text: string }

/** Why a row is not a trustworthy figure. A runtime const because i18n/keys.test.ts cannot see the `payroll.warn.${w}` template keys. */
export const PAY_WARNINGS = [
  'rate_missing',
  /** The company payroll_rate table has no row for this period, unlike `rate_missing` (this person has no rate). */
  'payroll_rate_missing',
  'shift_source_missing',
  'social_band_missing',
  'leave_factor_missing',
  'pay_basis_unrecognised',
  /** An assigned task rule set has no version in force on a worked day: that day priced 0 and the person is held at issue. */
  'task_rule_version_missing',
  /** Filed days no rate segment covers, listed in `PayRow.unpaid_filed_days`. Informational: the row still issues. */
  'unpaid_filed_days',
  /** The final run owes an unused-leave year-end payout but the person has no pay rate to price it with, so it is left out. */
  'cl_year_end_unpriced',
  /** A January final with no 31 Dec leave reset on record for last year, so nobody's unused PSL is paid: the row is held at issue until the reset runs. */
  'psl_year_end_reset_missing',
  /** An approved log entry falls on a date whose rate is not Daily+Hourly, so nothing priced it. */
  'contractor_entry_unpriced',
  /** This month's final was issued before the advance and deducted none, so the advance row is held at issue. */
  'final_issued',
  /** Leave taken since the break pay-out was raised leaves less AL or CL than it pays, so the final is held at issue. */
  'break_payout_balance_short',
  /** The leave balances were re-granted since the break pay-out was raised, so the final is held at issue. */
  'break_payout_leave_row_changed',
] as const

type PayWarning = (typeof PAY_WARNINGS)[number]

/** Warnings that describe a true figure rather than doubt it; mirrors the server's `INFO_WARNINGS`. */
const INFO_WARNINGS: readonly PayWarning[] = ['unpaid_filed_days']

/** The warnings that put a row in the "needs attention" list; informational ones are left out. */
export function doubtingWarnings(row: Pick<PayRow, 'warnings'>): PayWarning[] {
  return row.warnings.filter((w) => !INFO_WARNINGS.includes(w))
}

/** The interpolation a warning's sentence needs; an older server's missing date list gives count 0, so the sentence states nothing untold. */
export function warningArgs(
  row: Pick<PayRow, 'unpaid_filed_days'>,
  warning: PayWarning,
): { count: number; days: string } | undefined {
  if (warning !== 'unpaid_filed_days') return undefined
  const dates = row.unpaid_filed_days ?? []
  return { count: dates.length, days: dates.map((d) => d.slice(5)).join(', ') }
}

export interface PaySegment {
  type_of: string
  rate: number
  additional: number
  start: string
  end: string
  hours: number
  shifts: number
  segment_days: number
  month_days: number
  allowance: number
  /** `allowance` itemised by work mode (F, BTA, BTL, T); always present, possibly empty. */
  allowance_detail: Record<string, number>
  /** Whether this rate carries the transit allowance at all: "not entitled" vs "no transit work", which `allowance_detail` cannot tell apart. */
  transit_add: boolean
  gross: number
  /** A prior-month day paid late at the rate in force on it; without the flag it reads as a data error, not a correction. */
  carry_over?: boolean
  /** "no_prior_run" when carried because last month's final never paid this person; null/absent is an ordinary late day. */
  carry_reason?: string | null
  /** The dates behind a Daily segment's shift count, richest shape first; see `pages/payroll/shiftDates.ts`. */
  shift_dates?:
    | {
        date: string
        shifts: number | string | null
        mode: string | null
        /** The day's transit shift value, unweighted. */
        transit?: number | string | null
        /** The day's shift values unweighted, beside `shifts` as paid. Absent from an older server. */
        reported?: number | string | null
        /** `shifts` split by kind (D N L T DT), summing to it. Absent from an older server, which shows the bare number. */
        parts?: Record<string, number | string> | null
      }[]
    | null
  shift_days?: string[] | null
  transit_days?: string[] | null
  /** The calendar holidays inside this segment. */
  holiday_days?: string[]
  /** A carry segment's late-approved leave, by code; `null` when none. */
  carried_leave?: { code: string; days: number | string }[] | null
  /** Task work in this segment. Absent from an older server. */
  tasks?: number
  task_pay?: number
  task_detail?: TaskDayPrice[]
  /** One entry per paid task day, flat or tiered. Absent on an older run. */
  task_dates?: { day: string; count: number | string; rate: number | string; amount: number | string }[] | null
  task_unit_label?: string | null
  task_rule_set_name?: string | null
}

/** One priced task day: the count picked ONE tier, all paid at its rate. Numbers on a draft, strings on a frozen segment. */
interface TaskDayPrice {
  day: string
  count: number | string
  min_count: number | string
  rate: number | string
  pay: number | string
}

/** The task fields a draft and a frozen segment share, as either carries them. */
export interface TaskRowInput {
  tasks?: number | string | null
  task_pay?: number | string | null
  task_detail?: TaskDayPrice[] | null
  task_dates?: { day: string; count: number | string; rate: number | string; amount: number | string }[] | null
  task_unit_label?: string | null
  task_rule_set_name?: string | null
}

interface TaskRowView {
  count: number
  pay: number
  /** The set's own free-text label, or `null` so the caller falls back to the translated word. Never a translation key. */
  unitLabel: string | null
  setName: string | null
  /** Per-day pricing; empty on the flat path. */
  days: { day: string; count: number; rate: number; pay: number }[]
}

/** A segment's task row, or `null` when it recorded no task fields or earned nothing; a count with zero pay still shows. */
export function taskRow(segment: TaskRowInput): TaskRowView | null {
  if (segment.tasks == null && segment.task_pay == null) return null
  const count = Number(segment.tasks ?? 0)
  const pay = Number(segment.task_pay ?? 0)
  if (!Number.isFinite(count) || !Number.isFinite(pay)) return null
  if (count === 0 && pay === 0) return null
  const label = segment.task_unit_label?.trim()
  const setName = segment.task_rule_set_name?.trim()
  return {
    count,
    pay,
    unitLabel: label ? label : null,
    setName: setName ? setName : null,
    days: (segment.task_detail ?? []).map((d) => ({
      day: d.day,
      count: Number(d.count),
      rate: Number(d.rate),
      pay: Number(d.pay),
    })),
  }
}

/** ⛔ Keep these in `./frozenSegment`: this module imports `./api` → `msal.ts`, which cannot load under vitest's node environment. */
export type { ExcludedDay, FrozenSegment } from './frozenSegment'

/** A field added to `rate_breakdown` without a migration: `null` (issued before it shipped) must render differently from empty. */
export type Recorded<T> =
  | { known: false }
  | { known: true; empty: true }
  | { known: true; empty: false; value: T }

export function recordedList<T>(value: T[] | null | undefined): Recorded<T[]> {
  if (value == null) return { known: false }
  return value.length === 0 ? { known: true, empty: true } : { known: true, empty: false, value }
}

export function recordedMap<T extends Record<string, unknown>>(value: T | null | undefined): Recorded<T> {
  if (value == null) return { known: false }
  return Object.keys(value).length === 0
    ? { known: true, empty: true }
    : { known: true, empty: false, value }
}

export interface PayRow {
  /** False on an imported final whose source never recorded its figures. */
  figures_recorded?: boolean
  /** When a bank file last carried this row. Absent from an older server. */
  bank_exported_at?: string | null
  /** The payslip e-mail as the outbox holds it. Absent unless the row is Issued. */
  payslip?: { status: string; sent_at: string | null }
  /** Set when an admin deliberately unlocked this paid advance. */
  unlocked_at?: string | null
  unlock_reason?: string
  /** The exemption in force on the period's first day, per leg. */
  tax_exempt?: boolean
  social_exempt?: boolean
  employee_id: string
  name: string
  /** The live derivation. Empty on an issued row, whose working is `rate_breakdown`. */
  segments: PaySegment[]
  /** The working frozen at issue. Null on a draft, where `segments` is it. */
  rate_breakdown: FrozenSegment[] | null
  total_hours: number
  shift_count: number
  included_days: number
  al_days: number
  al_factor: number
  allowance_value: number
  /** Task pay, inside `gross` already. Absent from an older server. */
  task_value?: number
  gross: number
  bonus: number
  deduction: number
  custom_gross: number | null
  /** HR typed this row's gross, so social, tax and net come from it, not the timesheets. */
  calculated_by_hand?: boolean
  /** Whether the bank file can pay this person. No account number. Absent from an older server. */
  bank_account_status?: 'ok' | 'none' | 'no_primary' | null
  social: number
  tax: number
  net: number
  advance_paid: number
  /** On a final run, the advance's bonus or deduction added back, since `advance_paid` deducts the whole advance. */
  advance_adjustment: number
  /** A manual post-tax adjustment, signed (negative withholds); moves `payable` by its exact amount. */
  net_adjustment: number
  /** After tax while its tax treatment is parked: payable only, never gross. Final run only. */
  cl_year_end_pay: number
  cl_year_end_note: string
  cl_year_end_note_parts?: PayNotePart[]
  activity_bonus: number
  /** The final run marked to pay last year's activity fund: the months paid into it that year. */
  activity_months?: number[]
  activity_points?: number | string | null
  activity_rate?: number | string | null
  break_al_pay: number
  break_cl_psl_pay?: number
  break_cl_psl_note?: string
  break_cl_psl_note_parts?: PayNotePart[]
  /** Last month's window paid in this run because last month's final never paid this person. */
  no_run_window?: NoRunWindow | null
  issued_at: string | null
  payable: number
  soc_code: string
  soc_percent: number
  tax_percent: number
  status: 'Draft' | 'Issued'
  note: string
  warnings: PayWarning[]
  /** The ISO dates behind `unpaid_filed_days`. Absent from an older server. */
  unpaid_filed_days?: string[]
  /** `al_factor * al_days`, server computed, zero on a row that ended the month Fixed; `leaveTopUp` reads it. */
  leave_value?: number
  /** `staff_info.work_type`, '' when blank. Absent from an older server: read through `pages/payroll/payrollLeave.ts`. */
  work_type?: string
  /** Every approved leave overlapping the period up to this run's cut-off. */
  leaves?: PayLeave[]
  /** `days_in_period` summed per code. SPL is absent from an older server. */
  leave_counts?: { CL: number; PSL: number; SPL?: number; AL: number }
  /** The part of `leave_counts.AL` (what this run pays) paid ahead of its dates. Absent from an older server. */
  al_ahead_days?: number | string
  /** An approved leave in the window, or AL this run pays, so the row is not "Nothing logged". Absent from an older server. */
  has_leave?: boolean
  /** Days worked in each allowance work mode. Absent from an older server. */
  mode_days?: { F: number | string; T: number | string | null; BTA: number | string; BTL: number | string }
  /** Whether the rate carries the transit allowance; absent → read the segments; null when the segments differ (unknown, never false). */
  transit_add?: boolean | null
  /** Hour parts before the factors. `null` on an issued row frozen before they were recorded: not recorded, never zero. */
  hours_unweighted?: HourParts | null
  /** Each part x its factor; the parts sum to `total_hours`. */
  hours_weighted?: HourParts | null
  /** `staff_info` employment dates; `null` when not recorded. */
  employment_start?: string | null
  employment_end?: string | null
  /** The gross as the server's own sum of terms; Σ amount == gross. */
  gross_terms?: GrossTerm[]
  /** Display only: the bases of the two percentages. An issued row carries `tax_base` only (its cap was not frozen). */
  social_base?: number | string | null
  tax_base?: number | string | null
  social_cap?: number | string | null
}

/** How payroll treated one leave request in this run. */
export type LeaveTreatment =
  | 'paid'
  | 'partly_paid'
  | 'over_annual_cap'
  | 'annual_leave'
  | 'covered_by_fixed_salary'
  | 'not_paid'
  /** Annual leave in the window that another run pays (or paid). */
  | 'al_other_run'
  /** An issued row frozen before the leave ledger existed. */
  | 'not_recorded'

export interface PayLeave {
  /** Full label as stored, e.g. "CL - Casual Leave". */
  leave_type: string
  /** The prefix before " - ": "CL" | "PSL" | "AL" | "UL" | ... */
  code: string
  /** The REQUEST's own dates, not clipped to the period. */
  start_date: string
  end_date: string
  half_day: boolean
  /** Days inside the period up to this run's cut-off, half day = 0.5. */
  days_in_period: number
  /** What this run credited for it. */
  paid_days: number
  treatment: LeaveTreatment
  /** Annual leave this run paid ahead of its dates. */
  ahead?: boolean
  /** Dates of this request inside the window that ALSO earned work pay. */
  worked_dates?: string[]
}

/** One term of the gross formula. Decimals may arrive as strings. */
export interface GrossTerm {
  kind: string
  label: string | null
  qty: number | string | null
  unit: string | null
  rate: number | string | null
  amount: number | string
  /** The segment start, set only on a row with more than one rate segment. */
  since?: string | null
}

export interface HourParts {
  normal: number
  overtime: number
  night: number
  over_night: number
  transit: number
  leave: number
  holiday: number
}

interface NoRunWindow {
  year: number
  month: number
  days: number | string
  hours: number | string
  gross: number | string
  /** Carried AL days, already inside the row's `al_days`. */
  al_days?: number | string
}

interface PayPeriod {
  year: number
  month: number
  start: string
  advance_cut: string
  final_cut: string
  /** False when no pay-calendar row covered this month, so the cut-offs are the 9th/24th default and the page says so. */
  from_calendar: boolean
}

/** An actual day sent for approval after its period's cut-off; `next_period` when this month's final is already issued. */
export interface LateLog {
  day_id: string
  employee_id: string
  name: string
  day: string
  hours: string
  status: string
  submitted_at: string
  pays_in: 'this_period' | 'next_period'
  /** `YYYY-MM` of the month that pays it. */
  paid_in: string
}

/** An Actual day in the period that is Pending or Rejected, so not paid, though it would have counted if approved. */
export interface HeldDay {
  day_id: string
  employee_id: string
  day: string
  hours: string
  status: string
}

interface ExcludedPerson {
  employee_id: string
  name: string
  /** `left` or `volunteer` today; the page prints any other value as is. */
  reason: string
  /** `YYYY-MM-DD`; null unless the reason is `left`. */
  left_on: string | null
}

interface PayrollResponse {
  period: PayPeriod
  run: PayRun
  rows: PayRow[]
  /** Absent on an older payload: then the page says nothing, rather than "nobody". */
  excluded?: ExcludedPerson[]
  /** Null when the pay calendar cannot say which days this month holds; the page then says nothing, rather than "no late logs". */
  late_logs: LateLog[] | null
  /** The run window's held days; absent on an older payload, which then shows no badge rather than "none". */
  held_days?: HeldDay[]
  /** Held days over the whole payroll month, start to final cut-off, whatever the run. */
  held_month_count?: number
  month_lock?: MonthLock
  /** Absent on an older payload: then no mark control is shown. */
  activity_fund?: ActivityFundState
}

export interface ActivityFundState {
  enabled: boolean
  marked: boolean
  marked_month: number | null
  fund_year: number
}

/** A pay month changes until the end of the month after it, then is blocked for everyone. */
export interface MonthLock {
  state: 'open' | 'grace' | 'blocked'
  editable_until: string
}

/** `PayrollResponse`, as it actually arrives — see `FrozenSegmentWire`. */
interface RawPayrollResponse extends Omit<PayrollResponse, 'rows'> {
  rows: (Omit<PayRow, 'rate_breakdown'> & { rate_breakdown: FrozenSegmentWire[] | null })[]
}

export const payrollKeys = {
  month: (year: number, month: number, run: PayRun) =>
    ['payroll', year, month, run] as const,
  /** The month prefix, both runs: use it when an action on one run changes the other's figures (a void moves `advance_paid`). */
  monthAll: (year: number, month: number) => ['payroll', year, month] as const,
  /** ⛔ Not keyed on `run`: the route answers for both runs, so a `run` key would cache it twice and go stale after a void. */
  corrections: (employeeId: string, year: number, month: number) =>
    ['payroll', 'corrections', employeeId, year, month] as const,
}

export function usePayroll(year: number, month: number, run: PayRun) {
  return useQuery({
    queryKey: payrollKeys.month(year, month, run),
    queryFn: async () => {
      const { data } = await api.get<RawPayrollResponse>('/api/payroll', {
        params: { year, month, run },
      })
      return {
        ...data,
        rows: data.rows.map((row) => ({
          ...row,
          rate_breakdown: row.rate_breakdown?.map(toFrozenSegment) ?? null,
        })),
      }
    },
  })
}

export function useAdjustPay(year: number, month: number, run: PayRun) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      employeeId,
      payload,
    }: {
      employeeId: string
      payload: {
        bonus?: number
        deduction?: number
        custom_gross?: number
        /** No `clear_net_adjustment` twin: the PATCH treats `null`/absent as "leave it", so `0` clears it. */
        net_adjustment?: number
        note?: string
        clear_custom_gross?: boolean
      }
    }) =>
      (
        await api.patch<PayRow>(`/api/payroll/${employeeId}`, payload, {
          params: { year, month, run },
        })
      ).data,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: payrollKeys.month(year, month, run) }),
  })
}

/** One withdrawn payslip as the corrections route reports it: the figures it was issued with, not corrected ones. */
export interface Correction {
  id: string
  run: PayRun
  /** Always `'Void'`; carried so the row can say what it is without the UI asserting it. */
  status: string
  gross: string
  net: string
  payable: string
  /** Required server-side (`VoidIn`), so never blank. */
  void_reason: string
  /** The row this one replaced, or null on the month's first payslip; following it walks back to the original mistake. */
  supersedes_id: string | null
}

/** A month's withdrawn payslips for one person, oldest first; fetched only while its dialog is open. */
export function useCorrections(
  employeeId: string,
  year: number,
  month: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: payrollKeys.corrections(employeeId, year, month),
    enabled,
    queryFn: async () =>
      (
        await api.get<Correction[]>(`/api/payroll/${employeeId}/corrections`, {
          params: { year, month },
        })
      ).data,
  })
}

/** Withdraw an issued payslip and open a corrected draft. ⚠️ `run` is explicit: the route defaults to "advance". */
export function useVoidRun(year: number, month: number, run: PayRun) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      employeeId,
      reason,
      issuedAt,
    }: {
      employeeId: string
      reason: string
      issuedAt: string | null
    }) =>
      (
        await api.post<{ voided_id: string; successor_id: string; status: string }>(
          `/api/payroll/${employeeId}/void`,
          { reason, issued_at: issuedAt },
          { params: { year, month, run } },
        )
      ).data,
    onSuccess: (_result, { employeeId }) => {
      // Voiding one run changes the other run's `advance_paid`, so both runs' caches drop.
      queryClient.invalidateQueries({ queryKey: payrollKeys.monthAll(year, month) })
      queryClient.invalidateQueries({
        queryKey: payrollKeys.corrections(employeeId, year, month),
      })
    },
  })
}

/** What the run froze, and who it left out, by name. */
type IssueResult = {
  /** How many rows this call actually moved Draft → Issued. */
  issued: number
  /** Named, so a partial run can say who it marked calculated. */
  finalized: string[]
  /** Already frozen by an earlier call and left alone; normal when selections overlap. */
  already_issued: number
  /** People with nothing computable or logged; still Draft, so a later run picks them up. */
  skipped: string[]
  /** How many people were asked for. Zero means the whole run. */
  selected: number
  year: number
  month: number
  run: PayRun
}

/** Freeze a run, or its ready part; `employeeIds` empty means everybody, `skip` is left out of it. */
export function useIssuePay(year: number, month: number, run: PayRun) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ employeeIds = [], skip = [] }: { employeeIds?: string[]; skip?: string[] } = {}) =>
      (
        await api.post<IssueResult>(
          '/api/payroll/issue',
          { employee_ids: employeeIds, skip_employee_ids: skip },
          { params: { year, month, run } },
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll'] }),
  })
}

/** The bank-transfer batch and everyone it could not pay. `tsv` is built server-side, where the bank template's spec lives. */
export type BankExport = {
  columns: string[]
  rows: { account: string; name: string; currency: string; memo: string; amount: number }[]
  /** Named, not counted: a person missing from the file is a person unpaid. */
  withheld: { name: string; reason: string }[]
  total: number
  tsv: string
}

/** `already_sent` and `suppressed` are kept apart from `queued` so a no-op cannot read as success; a missing flag must not read as false. */
export type PayslipResult = {
  queued: number
  already_sent: number
  skipped: { name: string; reason: string }[]
  suppressed: boolean
}

/** Irreversible once the mail queues. `employeeIds` empty means everybody; a subset re-sends to corrected people only. */
export function useSendPayslips(year: number, month: number, run: PayRun) {
  return useMutation({
    mutationFn: async (employeeIds: string[] = []) =>
      (await api.post<PayslipResult>(
        '/api/payroll/payslips',
        { employee_ids: employeeIds },
        { params: { year, month, run } },
      )).data,
  })
}

/** One row of the pay calendar: which run of which month, and the two dates that bound it. */
export interface SalaryPeriod {
  id: string
  year: number
  month: number
  run: PayRun
  /** The day the money moves. */
  pay_date: string
  /** The last day of work this run counts. Stored, not derived from `pay_date`, so a past month reproduces exactly. */
  cut_off: string
}

export const periodKeys = {
  all: timeKeys.payPeriods,
}

export function usePayPeriods() {
  return useQuery({
    queryKey: periodKeys.all,
    queryFn: async () => (await api.get<SalaryPeriod[]>('/api/payroll/periods')).data,
  })
}

export interface PayCalendarDates {
  year: number
  month: number
  run: PayRun
  pay_date: string
  cut_off: string
}

/** The calendar as dates only, for callers without `salary.view`; generating invalidates it with the rest of `periodKeys`. */
export function usePayCalendarDates() {
  return useQuery({
    queryKey: [...periodKeys.all, 'pay-calendar'],
    queryFn: async () => (await api.get<PayCalendarDates[]>('/api/pay-calendar/periods')).data,
  })
}

/** The first run whose pay date has not passed, sorted here because `/periods` leaves a month's runs unordered; null past the last row. */
export function runDueNow(
  calendar: Pick<SalaryPeriod, 'year' | 'month' | 'run' | 'pay_date'>[],
  today: string,
): { month: string; run: PayRun } | null {
  const due = [...calendar]
    .sort(
      (a, b) =>
        a.pay_date.localeCompare(b.pay_date) ||
        (a.run === b.run ? 0 : a.run === 'advance' ? -1 : 1),
    )
    .find((row) => row.pay_date >= today)
  if (!due) return null
  return { month: `${due.year}-${String(due.month).padStart(2, '0')}`, run: due.run }
}

/** The five fields of a period; not a write payload (the PUT answers 410), kept as the shape `ProposedPeriod` extends. */
type SalaryPeriodIn = {
  year: number
  month: number
  run: PayRun
  pay_date: string
  cut_off: string
}

type ProposedPeriod = SalaryPeriodIn & {
  /** Already on the calendar; shown so the months read as continuous, skipped on write. */
  exists: boolean
  /** Non-empty when the holiday calendar lacks this year, so the pay date used the weekend-only rule. */
  note: string
}

/** The next months' cut-offs, unsaved: exactly what `useGeneratePeriods` will write. */
export function useProposedPeriods(months: number, enabled: boolean) {
  return useQuery({
    queryKey: ['payroll', 'periods', 'proposed', months],
    enabled,
    queryFn: async () =>
      (
        await api.get<ProposedPeriod[]>('/api/payroll/periods/proposed', {
          params: { months },
        })
      ).data,
  })
}

type GenerateResult = { written: number; months: string[]; skipped: number }

/** Extend the pay calendar by a count, not dates, so it cannot be hand-edited; 409 for a year with no confirmed holidays. */
export function useGeneratePeriods() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (months: number) =>
      (await api.post<GenerateResult>('/api/payroll/periods/generate', { months })).data,
    onSuccess: () =>
      Promise.all(
        [periodKeys.all, timeKeys.dayWindow].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  })
}

/** The payslip portal: the server reads the person off the session, so no employee id is sent and no `salary.view` is needed. */
export type MyPayslip = {
  /** False when the source never recorded social/tax/net. */
  figures_recorded?: boolean
  year: number
  month: number
  run: PayRun
  gross: number
  /** ⛔ `gross`, `social` and `tax` are cumulative month-to-date: never add the two runs'. Only `payable` is additive. */
  social: number
  tax: number
  net: number
  payable: number
}

type MyPayslipDetail = {
  /** False when the source never recorded social/tax/net. */
  figures_recorded?: boolean
  year: number
  month: number
  run: PayRun
  name: string
  covers: { start: string; cut_off: string }
  gross: number
  /** Set only when a manual override replaced the computed pay; `gross` is it plus the two settlements below. */
  custom_gross: number | null
  /** The resignation payout this final paid inside its gross; zero when none. */
  leave_payout: number
  /** The CL/PSL settled at a break start inside this gross, signed: a take-back is negative. */
  break_cl_psl_pay: number
  break_cl_psl_note?: string
  break_cl_psl_note_parts?: PayNotePart[]
  /** Annual leave paid out at the employee's request inside this gross; zero when none. */
  al_settlement_pay: number
  cl_year_end_pay: number
  cl_year_end_note: string
  cl_year_end_note_parts?: PayNotePart[]
  /** After tax, on payable only, as on the PDF. */
  activity_bonus: number
  /** The AL settled at a break start or Full Time switch, after tax, signed. */
  break_al_pay: number
  bonus: number
  deduction: number
  social: number
  soc_code: string
  soc_percent: number
  tax: number
  tax_percent: number
  net: number
  advance_paid: number
  /** The advance run's bonus or deduction added back so the final does not cancel it; zero when none. */
  advance_adjustment: number
  /** A manual post-tax adjustment, signed. See `PayRow.net_adjustment`. */
  net_adjustment: number
  payable: number
  total_hours: number
  shift_count: number
  included_days: number
  al_days: number
  al_factor: number
  /** `al_factor * al_days`, server computed: do not multiply them in the browser. Zero on a row that ended the month Fixed. */
  leave_value: number
  /** The AL dates this run paid; `null` when the issued row did not record them. */
  al_paid_days?: string[] | null
  allowance_value: number
  /** The working as frozen at issue — an issued row has no live segments. */
  rate_breakdown: FrozenSegment[] | null
  note: string
}

/** `MyPayslipDetail`, as it actually arrives — see `FrozenSegmentWire`. */
interface RawMyPayslipDetail extends Omit<MyPayslipDetail, 'rate_breakdown'> {
  rate_breakdown: FrozenSegmentWire[] | null
}

export function useMyPayslip(year: number, month: number, run: PayRun, enabled: boolean) {
  return useQuery({
    queryKey: ['me', 'payslips', year, month, run],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<RawMyPayslipDetail>(
        `/api/me/payslips/${year}/${month}/${run}`,
      )
      return { ...data, rate_breakdown: data.rate_breakdown?.map(toFrozenSegment) ?? null }
    },
  })
}

export function myPayslipPdfUrl(year: number, month: number, run: PayRun, lang: 'en' | 'mn' | 'ru') {
  return `/api/me/payslips/${year}/${month}/${run}/pdf?lang=${lang}`
}

/* ⛔ `PUT /api/payroll/social-insurance` adds or replaces the row for a `code` on its `effective_since` date. ⚠️ Decimals arrive as strings. */

/** A social-insurance band. `percent`/`tax_percent` are FRACTIONS on the wire ("0.1150" is 11.5%). */
export interface SocialRate {
  id: string
  code: string
  percent: string
  tax_percent: string
  description: string
  effective_since: string
}

type SocialRateIn = Omit<SocialRate, 'id'>

/** The company-wide per-day allowances and the contribution ceiling: money figures, not fractions. */
export interface CompanyRate {
  id: string
  effective_since: string
  field_allowance: string
  bta_allowance: string
  btl_allowance: string
  transit_allowance: string
  social_cap: string
}

export type CompanyRateIn = Omit<CompanyRate, 'id'>

const rateKeys = {
  social: ['payroll', 'social-insurance'] as const,
  company: ['payroll', 'rates'] as const,
}

export function useSocialRates(enabled = true) {
  return useQuery({
    queryKey: rateKeys.social,
    enabled,
    queryFn: async () => (await api.get<SocialRate[]>('/api/payroll/social-insurance')).data,
  })
}

/** Adds or replaces the row for a `code` on the given `effective_since` date. */
export function useSetSocialRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: SocialRateIn) =>
      (await api.put<SocialRate>('/api/payroll/social-insurance', body)).data,
    // An open draft's net pay just changed, so the whole payroll area is invalidated.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll'] }),
  })
}

export function useCompanyRates(enabled = true) {
  return useQuery({
    queryKey: rateKeys.company,
    enabled,
    queryFn: async () => (await api.get<CompanyRate[]>('/api/payroll/rates')).data,
  })
}

/** `GET /api/hr/task-rule-sets`, on the hr router so an `hr.edit`-only user's picker is not empty; archived sets included. */
const taskRuleKeys = { all: ['hr', 'task-rule-sets'] as const }

export function useTaskRuleSets(enabled = true) {
  return useQuery({
    queryKey: taskRuleKeys.all,
    enabled,
    queryFn: async () => (await api.get<TaskRuleSet[]>('/api/hr/task-rule-sets')).data,
  })
}

/** Every write below can move an open draft's pay, so the payroll area is invalidated too. */
function useTaskRuleMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskRuleKeys.all })
      queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
  })
}

export function useCreateTaskRuleSet() {
  return useTaskRuleMutation(
    async (body: { name: string; unit_label: string }) =>
      (await api.post<TaskRuleSet>('/api/hr/task-rule-sets', body)).data,
  )
}

export function usePatchTaskRuleSet() {
  return useTaskRuleMutation(
    async ({ id, ...body }: { id: string; name?: string; unit_label?: string; archived?: boolean }) =>
      (await api.patch<TaskRuleSet>(`/api/hr/task-rule-sets/${id}`, body)).data,
  )
}

/** Whole-version replace. A new date is additive; an existing one answers 409 unless `confirm_overwrite` is true. */
export function useSaveTaskRuleVersion() {
  return useTaskRuleMutation(
    async ({
      id,
      effective_since,
      tiers,
      confirm_overwrite,
    }: {
      id: string
      effective_since: string
      tiers: { min_count: number; rate: string }[]
      confirm_overwrite?: boolean
    }) =>
      (
        await api.put(`/api/hr/task-rule-sets/${id}/versions/${effective_since}`, {
          tiers,
          confirm_overwrite: confirm_overwrite ?? false,
        })
      ).data,
  )
}

export function useDeleteTaskRuleVersion() {
  return useTaskRuleMutation(
    async ({ id, effective_since }: { id: string; effective_since: string }) =>
      (await api.delete(`/api/hr/task-rule-sets/${id}/versions/${effective_since}`)).data,
  )
}

export function useSetCompanyRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: CompanyRateIn) =>
      (await api.put<CompanyRate>('/api/payroll/rates', body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll'] }),
  })
}


// Closing or reopening a payroll month also moves the time-sheet lock, so the day window is invalidated too.

export function usePayrollMonths(enabled = true) {
  return useQuery({
    queryKey: ['payroll-months'],
    queryFn: async () => (await api.get<PayrollMonth[]>('/api/payroll-months')).data,
    enabled,
  })
}

function usePayrollMonthAction(verb: 'close' | 'reopen') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ year, month, note = '' }: { year: number; month: number; note?: string }) =>
      (await api.post<PayrollMonth>(`/api/payroll-months/${year}/${month}/${verb}`, { note })).data,
    onSuccess: (answer) => {
      queryClient.invalidateQueries({ queryKey: ['payroll-months'] })
      queryClient.invalidateQueries({ queryKey: timeKeys.dayWindow })
      // A page open since before the lock learns it here, so its Close button stops offering.
      if (verb === 'close' && closeWroteNothing(answer)) queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
  })
}

export const useClosePayrollMonth = () => usePayrollMonthAction('close')
export const useReopenPayrollMonth = () => usePayrollMonthAction('reopen')

type ActivityWorking = { months: number[]; points: number; rate: number; total: number }

/** The computed activity-fund bonus behind a row of the marked final run, or null when this run does not pay it. */
export function activityWorking(
  row: Pick<PayRow, 'activity_months' | 'activity_points' | 'activity_rate' | 'activity_bonus'>,
): ActivityWorking | null {
  if (row.activity_points == null || row.activity_rate == null) return null
  return {
    months: row.activity_months ?? [],
    points: Number(row.activity_points),
    rate: Number(row.activity_rate),
    total: Number(row.activity_bonus ?? 0),
  }
}

export type ActivityMarkControl =
  | { kind: 'hidden' }
  | { kind: 'off' }
  | { kind: 'elsewhere'; month: number }
  | { kind: 'mark'; blocked: null | 'future' | 'issued' | 'locked' }
  | { kind: 'marked'; blocked: null | 'issued' | 'locked' }

/** What the final run offers for the once-a-year activity-fund mark; the server re-checks every refusal. */
export function activityMarkControl(
  state: ActivityFundState | undefined,
  ctx: { run: PayRun; year: number; thisYear: number; mayEdit: boolean; monthOpen: boolean; anyIssued: boolean },
): ActivityMarkControl {
  if (!state || ctx.run !== 'final' || !ctx.mayEdit) return { kind: 'hidden' }
  if (state.marked) return { kind: 'marked', blocked: ctx.anyIssued ? 'issued' : ctx.monthOpen ? null : 'locked' }
  if (!state.enabled) return { kind: 'off' }
  if (state.marked_month != null) return { kind: 'elsewhere', month: state.marked_month }
  const blocked = ctx.year > ctx.thisYear ? 'future' : ctx.anyIssued ? 'issued' : ctx.monthOpen ? null : 'locked'
  return { kind: 'mark', blocked }
}

function useActivityFundMarkAction(verb: 'put' | 'delete') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ year, month }: { year: number; month: number }) =>
      (await api.request<ActivityFundState>({ method: verb, url: '/api/payroll/activity-fund-mark', params: { year, month } })).data,
    onSuccess: (_data, { year, month }) =>
      queryClient.invalidateQueries({ queryKey: payrollKeys.monthAll(year, month) }),
  })
}

export const useMarkActivityFund = () => useActivityFundMarkAction('put')
export const useUnmarkActivityFund = () => useActivityFundMarkAction('delete')
